import { createAction, props } from '@ngrx/store';
import { EntityMap, EntityMapOne, Update } from '@ngrx/entity';
import { IDjangoPaginationData } from '@shared/ui-components/au-paginator/pagination-page.info';
import { AuGqlError } from '@app/shared';
import {
	ICmmsIncidentsQueryVariables,
	IIncident,
	IIncidentCreateVariables,
	IIncidentPayload,
} from '@app/pages/cmms/interfaces/incidents.interface';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { IResultData } from '@app/pages/cmms/interfaces/incident-detection.interfaces';

export const loadDefaultIncidents = createAction(
	'[INCIDENTS] Load incidents with default variables'
);

export const startLoadingIncidents = createAction(
	'[INCIDENTS] Start loading incidents',
	props<{ variables: ICmmsIncidentsQueryVariables }>()
);

export const incidentsLoaded = createAction(
	'[INCIDENTS] Incidents loaded',
	props<{
		incidentPagination: IDjangoPaginationData;
		incidents: IIncident[];
	}>()
);

export const incidentsFailedToLoad = createAction('[INCIDENTS] Incidents failed to load');

export const storeIncidentsQueryVariables = createAction(
	'[INCIDENTS] Store incidents query variables',
	props<{ variables: ICmmsIncidentsQueryVariables }>()
);

export const dropIncidentsQueryVariables = createAction(
	'[INCIDENTS] Drop incidents query variables'
);

export const setIncidentForEditing = createAction(
	'[INCIDENTS] Set incident for editing',
	props<{
		incidentEdit: IIncident;
	}>()
);

export const startEditIncident = createAction(
	'[INCIDENTS] Start edit incident',
	props<{
		incident: Partial<IIncidentPayload>;
	}>()
);
export const editIncidentSuccess = createAction(
	'[INCIDENTS] Edit incident success',
	props<{
		incident: IIncident;
	}>()
);
export const setIncidents = createAction(
	'[INCIDENTS] Set incidents',
	props<{ incidents: IIncident[] }>()
);
export const startCreateIncident = createAction(
	'[INCIDENTS] Start create incident',
	props<{
		variables: IIncidentCreateVariables;
		skipTableReload?: boolean;
		closeSidebar?: boolean;
		dexterSource?: {
			fileId: string;
			result: IResultData[];
			resultId: string;
		};
	}>()
);
export const createIncidentSuccess = createAction(
	'[INCIDENTS] Create incident success',
	props<{
		incident: IIncident;
		skipTableReload?: boolean;
		dexterSource?: {
			fileId: string;
			result: IResultData[];
			resultId: string;
		};
	}>()
);
export const createIncidentError = createAction(
	'[INCIDENTS] Create incident error',
	props<{ error?: AuGqlError }>()
);
export const addIncident = createAction(
	'[INCIDENTS] Add incident',
	props<{ incident: IIncident }>()
);
export const setIncident = createAction(
	'[INCIDENTS] Set incident',
	props<{ incident: IIncident }>()
);
export const upsertIncident = createAction(
	'[INCIDENTS] Upsert incident',
	props<{ incident: IIncident }>()
);
export const addIncidents = createAction(
	'[INCIDENTS] Add incidents',
	props<{ incidents: IIncident[] }>()
);
export const upsertIncidents = createAction(
	'[INCIDENTS] Upsert incidents',
	props<{ incidents: IIncident[] }>()
);
export const updateIncident = createAction(
	'[INCIDENTS] Update incident',
	props<{ update: Update<IIncident> }>()
);
export const updateIncidents = createAction(
	'[INCIDENTS] Update incidents',
	props<{ updates: Update<IIncident>[] }>()
);
export const mapIncident = createAction(
	'[INCIDENTS] Map incident',
	props<{ entityMap: EntityMapOne<IIncident> }>()
);

export const mapIncidents = createAction(
	'[INCIDENTS] Map incidents',
	props<{ entityMap: EntityMap<IIncident> }>()
);
export const startDeletingIncident = createAction(
	'[INCIDENTS] Start deleting incident',
	props<{ pk: string; closeSidebar?: boolean }>()
);
export const deletingIncidentSuccess = createAction(
	'[INCIDENTS] Deleting incident success',
	props<{ pk: string }>()
);
export const deleteIncident = createAction(
	'[INCIDENTS] Delete incident',
	props<{ pk: string }>()
);
export const deleteIncidents = createAction(
	'[INCIDENTS] Delete incidents',
	props<{ pks: string[] }>()
);

export const openIncidentWorkspaceFromOutside = createAction(
	'[INCIDENTS] Open incident workspace from outside',
	props<{ pk: string }>()
);

export const openNewIncidentWorkspaceFromOutside = createAction(
	'[INCIDENTS] Open new incident workspace from outside',
	// this syntax is used to make payload optional
	(incidentData = null) => ({ incidentData })
);

export const setIncidentsTableConfig = createAction(
	'[INCIDENTS] Set incidents table config',
	props<{
		isExportable: boolean;
		columns: ICMMSColumn[];
	}>()
);

export const setIncidentsSelectedCalendarDate = createAction(
	'[INCIDENTS] Set incidents selected calendar date',
	props<{
		selectedDate: string; // '2023-10-25'
	}>()
);

export const addIncidentExternalData = createAction(
	'[INCIDENTS] Add incident external data',
	(incidentData = null) => ({ incidentData })
);

export const clearIncidentExternalData = createAction(
	'[INCIDENTS] Clear incident external data'
);
